import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '../../../shared/components/ErrorBoundary/ErrorBoundary.js';
import { toAdminFacingErrorMessage } from '../../../shared/errors/toAdminFacingErrorMessage.js';

interface ChartErrorBoundaryProps {
  children: ReactNode;
  sectionName: string;
  onRetry?: () => void;
}

/**
 * Granular error boundary for individual chart sections
 */
export function ChartErrorBoundary({ children, sectionName, onRetry }: ChartErrorBoundaryProps) {
  const { t } = useTranslation('admin');

  return (
    <ErrorBoundary
      fallback={(error, resetError) => (
        <div className="an-error-boundary" role="alert">
          <div className="an-error-boundary-icon">⚠️</div>
          <div className="an-error-boundary-content">
            <h3 className="an-error-boundary-title">
              {t('analytics.chartErrorBoundary.title', { section: sectionName })}
            </h3>
            <p className="an-error-boundary-message">
              {toAdminFacingErrorMessage(error, {
                fallback: t('analytics.chartErrorBoundary.message'),
              })}
            </p>
            <div className="an-error-boundary-actions">
              {onRetry ? (
                <button
                  onClick={() => {
                    resetError();
                    onRetry();
                  }}
                  className="an-error-boundary-retry"
                  type="button"
                >
                  {t('analytics.chartErrorBoundary.retry')}
                </button>
              ) : (
                <button onClick={resetError} className="an-error-boundary-retry" type="button">
                  {t('analytics.chartErrorBoundary.retry')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
